/**
 * TrackingLinksCard — individuelle Kurz-Links pro Firma/Kanal.
 * Jeder Link zaehlt Klicks und haengt UTM an → Quelle in Analytics + Leads nachvollziehbar.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Copy, Download, Layers, Link2, Loader2, Plus, QrCode, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = ["Instagram", "Instagram Story", "Instagram Bio", "Facebook", "WhatsApp", "LinkedIn", "TikTok", "Google Ads", "Google Business", "E-Mail-Signatur", "Flyer", "Plakat", "QR-Code", "Visitenkarte", "Zeitung / Inserat", "Sonstiges"];

const VARIANT_CHANNELS = ["Instagram", "Facebook", "WhatsApp", "LinkedIn", "TikTok", "Google Ads", "Flyer", "Zeitung / Inserat"];

export const TrackingLinksCard = ({ companyId }) => {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ name: "", channel: "Flyer", target_url: "", slug: "" });
  const [busy, setBusy] = useState(false);
  const [variantsOpen, setVariantsOpen] = useState(false);
  const [vForm, setVForm] = useState({ name: "", target_url: "", slug: "", channels: ["Instagram", "Facebook", "WhatsApp"] });
  const [vBusy, setVBusy] = useState(false);
  const [qrLink, setQrLink] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);
  const [qrBusy, setQrBusy] = useState(false);
  const [qrBranded, setQrBranded] = useState(true);

  const loadQrPreview = async (it, branded) => {
    setQrUrl(null);
    try {
      const r = await axios.get(`${API}/tracking-links/${it.id}/qr`, {
        params: { size: 600, plain: branded ? 0 : 1 }, responseType: "blob", withCredentials: true,
      });
      setQrUrl(URL.createObjectURL(r.data));
    } catch { toast.error("QR-Code konnte nicht erstellt werden"); setQrLink(null); }
  };

  const openQr = async (it) => {
    setQrLink(it);
    loadQrPreview(it, qrBranded);
  };

  const downloadQr = async () => {
    if (!qrLink) return;
    setQrBusy(true);
    try {
      const r = await axios.get(`${API}/tracking-links/${qrLink.id}/qr`, {
        params: { size: 1200, plain: qrBranded ? 0 : 1 }, responseType: "blob", withCredentials: true,
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `qr_${qrLink.code}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("QR-Code heruntergeladen (druckfertig, 300 dpi)");
    } catch { toast.error("Download fehlgeschlagen"); }
    finally { setQrBusy(false); }
  };

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const r = await axios.get(`${API}/tracking-links`, { params: { company_id: companyId }, withCredentials: true });
      setItems(r.data.items || []);
    } catch { setItems([]); }
  }, [companyId]);

  useEffect(() => { setItems(null); load(); }, [companyId, load]);

  const create = async () => {
    if (!form.name.trim() || !form.target_url.trim()) {
      toast.error("Name und Ziel-URL erforderlich");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/tracking-links`, { company_id: companyId, ...form }, { withCredentials: true });
      toast.success("Tracking-Link erstellt");
      setForm({ name: "", channel: form.channel, target_url: form.target_url, slug: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erstellen fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const createVariants = async () => {
    if (!vForm.name.trim() || !vForm.target_url.trim() || vForm.channels.length === 0) {
      toast.error("Name, Ziel-URL und mindestens 1 Kanal erforderlich");
      return;
    }
    setVBusy(true);
    try {
      const r = await axios.post(`${API}/tracking-links/variants`, { company_id: companyId, ...vForm }, { withCredentials: true });
      const n = (r.data.created || []).length;
      toast.success(`${n} Kanal-Varianten erstellt${r.data.skipped?.length ? ` (${r.data.skipped.length} übersprungen — Name schon vergeben)` : ""}`);
      setVariantsOpen(false);
      setVForm({ name: "", target_url: "", slug: "", channels: ["Instagram", "Facebook", "WhatsApp"] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erstellen fehlgeschlagen"); }
    finally { setVBusy(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Link «${it.name}» löschen? Bereits verteilte QR-Codes/Flyer mit diesem Link funktionieren dann nicht mehr.`)) return;
    try {
      await axios.delete(`${API}/tracking-links/${it.id}`, { withCredentials: true });
      toast.success("Link gelöscht");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link kopiert");
    } catch {
      toast.error("Kopieren nicht erlaubt — Link manuell markieren");
    }
  };

  return (
    <Card data-testid="tracking-links-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Link2 className="w-4 h-4" /> Individuelle Tracking-Links
        </CardTitle>
        <p className="text-xs text-slate-500">
          Erstelle pro Kanal (Flyer, QR-Code, Instagram-Bio …) eine eigene Kurz-Adresse. Jeder Klick wird gezählt und die Quelle erscheint in der Besucher-Herkunft und bei den Leads — so weisst du genau, woher deine Kunden kommen.
        </p>
        <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1.5" data-testid="tracking-links-pretty-hint">
          Neu: Jeder Link hat eine schöne Adresse auf der <b>eigenen Firmen-Domain</b> (z.B. <code>www.deine-firma.ch/go/probetraining</code>) — vertrauenswürdig für Kunden, volle Messung bleibt erhalten. Voraussetzung: Das zentrale Lade-Script ist auf der Website eingebunden (siehe Website-Integration oben).
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <label className="text-xs text-[var(--studio-text-muted)]">Name (z.B. Frühlingsaktion)</label>
            <Input className="h-8 w-48 text-sm" value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              data-testid="tracking-link-name-input" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-[var(--studio-text-muted)]">Kanal</label>
            <Select value={form.channel} onValueChange={(v) => setForm((f) => ({ ...f, channel: v }))}>
              <SelectTrigger className="h-8 w-44 text-sm" data-testid="tracking-link-channel-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CHANNELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 flex-1 min-w-[220px]">
            <label className="text-xs text-[var(--studio-text-muted)]">Ziel-URL (deine Website-Seite)</label>
            <Input className="h-8 text-sm" placeholder="https://www.deine-firma.ch/angebot" value={form.target_url}
              onChange={(e) => setForm((f) => ({ ...f, target_url: e.target.value }))}
              data-testid="tracking-link-url-input" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-[var(--studio-text-muted)]">Wunsch-Name im Link (optional)</label>
            <Input className="h-8 w-44 text-sm" placeholder="z.B. probetraining" value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              data-testid="tracking-link-slug-input" />
          </div>
          <Button size="sm" className="h-8 gap-1" onClick={create} disabled={busy} data-testid="tracking-link-create-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Link erstellen
          </Button>
          <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => setVariantsOpen(true)} data-testid="tracking-link-variants-btn">
            <Layers className="w-3.5 h-3.5" /> Kanal-Varianten
          </Button>
        </div>

        {items === null && <div className="py-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}
        {items?.length === 0 && <p className="text-xs text-slate-400" data-testid="tracking-links-empty">Noch keine Tracking-Links für diese Firma.</p>}
        {items?.map((it) => (
          <div key={it.id} className="flex flex-wrap items-center gap-2 border border-[var(--studio-border)] rounded-lg px-3 py-2 text-sm" data-testid={`tracking-link-row-${it.code}`}>
            <span className="font-medium">{it.name}</span>
            <Badge variant="outline" className="text-[10px]">{it.channel}</Badge>
            <code className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-1.5 py-0.5 truncate max-w-[300px]" title={it.pretty_url || it.short_url}>
              {(it.pretty_url || it.short_url).replace(/^https?:\/\//, "")}
            </code>
            <Button size="sm" variant="ghost" className="h-6 px-1.5" title="Schönen Link kopieren" onClick={() => copy(it.pretty_url || it.short_url)} data-testid={`tracking-link-copy-${it.code}`}>
              <Copy className="w-3 h-3" />
            </Button>
            {it.pretty_url && (
              <Button size="sm" variant="ghost" className="h-6 px-1.5 text-slate-400" title={`Direktlink kopieren: ${it.short_url}`} onClick={() => copy(it.short_url)} data-testid={`tracking-link-copy-direct-${it.code}`}>
                <Link2 className="w-3 h-3" />
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-6 px-1.5" title="QR-Code für Flyer/Plakate" onClick={() => openQr(it)} data-testid={`tracking-link-qr-${it.code}`}>
              <QrCode className="w-3 h-3" />
            </Button>
            <div className="flex-1" />
            <span className="text-xs text-slate-500"><b className="text-[var(--studio-text)]">{it.clicks}</b> Klicks</span>
            <span className="text-xs text-slate-500"><b className="text-[var(--studio-text)]">{it.leads}</b> Leads</span>
            {it.last_click_at && <span className="text-[10px] text-slate-400">zuletzt {new Date(it.last_click_at).toLocaleDateString("de-CH")}</span>}
            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-red-500" onClick={() => remove(it)} data-testid={`tracking-link-delete-${it.code}`}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        ))}

        <Dialog open={variantsOpen} onOpenChange={(o) => !o && setVariantsOpen(false)}>
          <DialogContent className="max-w-md" data-testid="tracking-link-variants-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <Layers className="w-4 h-4" /> Kanal-Varianten erstellen
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-xs text-slate-500">
                Erstellt pro Kanal einen eigenen Mess-Link mit demselben Ziel — z.B. <code>/go/probetraining-insta</code>, <code>/go/probetraining-fb</code>, <code>/go/probetraining-wa</code>. So siehst du genau, welcher Kanal am meisten bringt.
              </p>
              <div className="space-y-1">
                <label className="text-xs text-[var(--studio-text-muted)]">Name der Aktion</label>
                <Input className="h-8 text-sm" placeholder="z.B. Probetraining" value={vForm.name}
                  onChange={(e) => setVForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="variants-name-input" />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-[var(--studio-text-muted)]">Ziel-URL (gleich für alle Kanäle)</label>
                <Input className="h-8 text-sm" placeholder="https://app.deine-firma.ch/buchen" value={vForm.target_url}
                  onChange={(e) => setVForm((f) => ({ ...f, target_url: e.target.value }))}
                  data-testid="variants-url-input" />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-[var(--studio-text-muted)]">Basis-Name im Link (optional, sonst aus Name)</label>
                <Input className="h-8 text-sm" placeholder="z.B. probetraining" value={vForm.slug}
                  onChange={(e) => setVForm((f) => ({ ...f, slug: e.target.value }))}
                  data-testid="variants-slug-input" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-[var(--studio-text-muted)]">Kanäle</label>
                <div className="grid grid-cols-2 gap-1.5">
                  {VARIANT_CHANNELS.map((c) => (
                    <label key={c} className="flex items-center gap-2 text-sm cursor-pointer select-none border border-[var(--studio-border)] rounded-md px-2 py-1.5">
                      <input type="checkbox" checked={vForm.channels.includes(c)}
                        data-testid={`variants-channel-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        onChange={(e) => setVForm((f) => ({
                          ...f,
                          channels: e.target.checked ? [...f.channels, c] : f.channels.filter((x) => x !== c),
                        }))} />
                      {c}
                    </label>
                  ))}
                </div>
              </div>
              <Button className="w-full gap-1.5" onClick={createVariants} disabled={vBusy} data-testid="variants-create-btn">
                {vBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
                {vForm.channels.length} Varianten erstellen
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={!!qrLink} onOpenChange={(o) => { if (!o) { setQrLink(null); setQrUrl(null); } }}>
          <DialogContent className="max-w-sm" data-testid="tracking-link-qr-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <QrCode className="w-4 h-4" /> QR-Code: {qrLink?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center gap-3">
              {qrUrl ? (
                <img src={qrUrl} alt="QR-Code" className="w-64 h-64 border border-[var(--studio-border)] rounded-lg" data-testid="tracking-link-qr-image" />
              ) : (
                <div className="w-64 h-64 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
              )}
              <code className="text-[11px] text-slate-500 break-all text-center">{qrLink?.short_url}</code>
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
                <input type="checkbox" checked={qrBranded} data-testid="tracking-link-qr-branded-toggle"
                  onChange={(e) => { setQrBranded(e.target.checked); if (qrLink) loadQrPreview(qrLink, e.target.checked); }} />
                Mit Firmenlogo &amp; Markenfarbe (aus den Firmen-Einstellungen)
              </label>
              <p className="text-xs text-slate-500 text-center">
                Scan führt auf <b>{qrLink?.target_url}</b> — jeder Scan wird als Klick gezählt und die Quelle «{qrLink?.channel}» ist bei Besuchern und Leads nachvollziehbar.
              </p>
              <Button className="w-full gap-2" onClick={downloadQr} disabled={qrBusy || !qrUrl} data-testid="tracking-link-qr-download-btn">
                {qrBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                PNG herunterladen (druckfertig, 1200px / 300 dpi)
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default TrackingLinksCard;
